import { Button, ButtonGroup, Chip, DialogContent, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, TextField, Typography, useTheme } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react'
import ManageOrganizationClient, { InvitationItem, OrganizationUser } from '../apiClients/ManageOrganizationClient';
import { OrgAssociationType, Securables } from '../Enums';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EmployeeEditorPopup from './UserEditorPopup'
import { useAccessInfo } from '../contexts/AccessTokenProvider';
import BusySpinner from '../components/BusySpinner';
import { useErrorDisplay } from '../components/ErrorDisplay';
import IconButtonEx from '../components/IconButtonEx';
import TZGravatar from '../components/TZGravatar';
import { ifTrue, IsEmail } from '../Helpers';
import { DataGridPro, GridToolbarContainer, GridToolbarFilterButton, GridToolbarDensitySelector } from '@mui/x-data-grid-pro';
import ConditionalDisplay from '../components/ConditionalDisplay';
import PanelSegment from '../components/PanelSegment';
import { useSearchBox } from '../components/SearchBox';
import I18NText from '../components/I18NText';
import { useMainDisplayHeight } from '../components/WindowDimensions';
import { I18N } from '../I18N';
import PageTitle from '../components/PageTitle';
import MobileEntityCard from '../components/responsive/MobileEntityCard';
import ResponsiveCollection from '../components/responsive/ResponsiveCollection';
import { ResponsiveDialog, ResponsiveDialogHeader, StickyDialogActions } from '../components/responsive';
import useViewportMode from '../hooks/useViewportMode';

export interface OrgUsersDisplayProps {
    assocationType: OrgAssociationType;
}
type OrgUserRow = (OrganizationUser | InvitationItem) & {
    DisplayName?: string;
    Status: string;
    isInvite: boolean;
};

const OrgUsersDisplay = ({ assocationType }: OrgUsersDisplayProps) => {
    const theme = useTheme();
    const initialized = useRef<boolean>();
    const [orgUsers, setOrgUsers] = useState<OrganizationUser[]>([]);
    const [orgInvites, setOrgInvites] = useState<InvitationItem[]>([]);
    const [busy, setBusy] = useState<boolean>(false);

    const [inviteEmails, setInviteEmails] = useState<string>();
    const [roleTemplate, setRoleTemplate] = useState<string>("User");

    const [showInvitePopup, setShowInvitePopup] = useState<boolean>(false);
    const [ErrorDisplay, setError] = useErrorDisplay();
    const { accessInfo, hasRole } = useAccessInfo();
    const [SearchBox, searchFilter] = useSearchBox();
    const viewport = useViewportMode();
    const mainDisplayHeight = useMainDisplayHeight();

    const loadData = async () => {
        try {
            setBusy(true);
            setOrgUsers(await new ManageOrganizationClient().GetUsers(assocationType));
            setOrgInvites(await new ManageOrganizationClient().GetInvitationsForOrganization(assocationType));
        } catch (error) {
            setError(error, "Failed to load OrgUsers");
        } finally {
            setBusy(false);
        }
    }

    useEffect(() => {
        if (initialized.current === true)
            return;
        initialized.current = true;
        loadData();
    });

    const handleDelete = async (user: OrganizationUser) => {
        try {
            setBusy(true);
            await new ManageOrganizationClient().RemoveUserFromOrganization(user.Id, assocationType);
            await loadData();
        } catch (error) {
            setError(error, "Failed to delete user");
        } finally {
            setBusy(false);
        }
    }

    const handleDeleteInvite = async (invitation: InvitationItem) => {
        try {
            setBusy(true);
            await new ManageOrganizationClient().DeleteInvitation(invitation.Id);
            await loadData();
        } catch (error) {
            setError(error, "Failed to delete user");
        } finally {
            setBusy(false);
        }
    }

    const handleShowAdd = async () => {
        setInviteEmails(null);
        setShowInvitePopup(true);
    }

    const handleInvite = async () => {
        try {
            setBusy(true);
            setShowInvitePopup(false);

            let role = roleTemplate;
            if (assocationType === OrgAssociationType.Consultant)
                role = "Consultant";

            const inviteContext = {
                EmailAddresses: inviteEmails.split(","),
                RoleTemplate: role
            }

            await new ManageOrganizationClient().InviteUsersToOrganization(assocationType, inviteContext);
            await loadData();
            console.log(`__ Synergist.UI -- Invite: ${inviteEmails}`);
        } catch (error) {
            setError(error, "Failed to Invite user");
        } finally {
            setBusy(false);
        }
    }

    const visibleRows = searchFilter([
        ...orgInvites.map(item => ({ ...item, DisplayName: '', Status: I18N.Translate('Invited'), isInvite: true })),
        ...orgUsers.map(item => ({ ...item, Status: I18N.Translate('Active'), isInvite: false })),
    ]) as OrgUserRow[];

    const canEdit = hasRole(assocationType === OrgAssociationType.Employee
        ? Securables.organization_employee
        : Securables.organization_consultant);
    const canDeleteInvite = hasRole(assocationType === OrgAssociationType.Employee
        ? Securables.organization_employee_invite_delete
        : Securables.organization_consultant_invite_delete);
    const canDeleteUser = hasRole(assocationType === OrgAssociationType.Employee
        ? Securables.organization_employee_delete
        : Securables.organization_consultant_delete);

    const renderUserCard = (row: OrgUserRow) => {
        const displayName = row.DisplayName || row.EmailAddress;
        return <MobileEntityCard
            ariaLabel={`${row.isInvite ? 'Invitation' : 'Person'}: ${displayName}`}
            title={displayName}
            subtitle={row.EmailAddress}
            status={<Chip size="small" label={row.Status} color={row.isInvite ? 'warning' : 'success'} variant="outlined" />}
            fields={row.isInvite ? [{ label: 'Role', value: (row as InvitationItem).RoleTemplate || '—', fullWidth: true }] : []}
            actions={
                <>
                    {!row.isInvite && canEdit && (
                        <EmployeeEditorPopup
                            icon={<EditIcon fontSize="small" htmlColor="green" />}
                            user={row as OrganizationUser}
                            associationType={assocationType}
                            aria-label={`Edit person: ${displayName}`}
                        />
                    )}
                    {row.isInvite && canDeleteInvite && (
                        <IconButtonEx
                            hoverContent="Delete"
                            aria-label={`Delete invitation: ${row.EmailAddress}`}
                            size="small"
                            message="Are you sure you want to terminate this invite"
                            onClick={() => handleDeleteInvite(row as InvitationItem)}
                        >
                            <DeleteIcon htmlColor="red" />
                        </IconButtonEx>
                    )}
                    {!row.isInvite && accessInfo.email !== row.EmailAddress && canDeleteUser && (
                        <IconButtonEx
                            hoverContent="Delete"
                            aria-label={`Remove person: ${displayName}`}
                            size="small"
                            message="Are you sure you want to remove this user"
                            onClick={() => handleDelete(row as OrganizationUser)}
                        >
                            <DeleteIcon fontSize="small" htmlColor="red" />
                        </IconButtonEx>
                    )}
                </>
            }
        />;
    };

    const desktopGrid = (
        <div style={{ height: mainDisplayHeight, width: '100%' }}>
            <DataGridPro
                density="compact"
                rows={visibleRows.map(x => { return { ...x, id: x.Id } })}
                columns={[
                    {
                        field: 'Action',
                        sortable: false,
                        filterable: false,
                        resizable: false,
                        width: 70,
                        align: 'center',
                        disableColumnMenu: true,
                        renderCell: ({ row }) => row.isInvite ? null :
                            <ButtonGroup>
                                <ConditionalDisplay ifTrue={hasRole(assocationType === OrgAssociationType.Employee ? Securables.organization_employee : Securables.organization_consultant)}>
                                    <EmployeeEditorPopup icon={<EditIcon fontSize='small' htmlColor='green' />} user={row as OrganizationUser} associationType={assocationType} />
                                </ConditionalDisplay>
                            </ButtonGroup>
                    },
                    {
                        field: 'avatar',
                        headerName: '',
                        width: 70,
                        align: 'center',
                        disableColumnMenu: true,
                        sortable: false,
                        renderCell: ({ row }) => <TZGravatar email={row.EmailAddress} />
                    },
                    {
                        field: 'DisplayName',
                        headerName: 'Name',
                        flex: 1
                    },
                    {
                        field: 'EmailAddress',
                        headerName: 'Email',
                        flex: 1
                    },
                    {
                        field: 'Status',
                        headerName: 'Status',
                        flex: 1
                    },
                    {
                        field: 'Delete',
                        sortable: false,
                        filterable: false,
                        resizable: false,
                        disableColumnMenu: true,
                        width: 70,
                        align: 'center',
                        hide: false,
                        renderCell: ({ row }) => row.isInvite
                            ? ifTrue(hasRole(assocationType === OrgAssociationType.Employee ? Securables.organization_employee_invite_delete : Securables.organization_consultant_invite_delete), () => <IconButtonEx hoverContent='Delete' size='small' message='Are you sure you want to terminate this invite' onClick={() => handleDeleteInvite(row as InvitationItem)}><DeleteIcon htmlColor='red' /></IconButtonEx>)
                            : ifTrue(accessInfo.email !== row.EmailAddress, () => ifTrue(hasRole(assocationType === OrgAssociationType.Employee ? Securables.organization_employee_delete : Securables.organization_consultant_delete), () => <IconButtonEx hoverContent='Delete' size='small' message='Are you sure you want to remove this user' onClick={() => handleDelete(row as OrganizationUser)}><DeleteIcon fontSize='small' htmlColor='red' /></IconButtonEx>))
                    }
                ]}
                components={{
                    Toolbar: () => (
                        <GridToolbarContainer>
                            <GridToolbarFilterButton onResize={undefined} onResizeCapture={undefined} />
                            <GridToolbarDensitySelector onResize={undefined} onResizeCapture={undefined} />
                        </GridToolbarContainer>
                    )
                }}
            />
        </div>
    );

    return <>
        <ErrorDisplay />
        <BusySpinner busy={busy}>
            <PanelSegment titleContent={<PageTitle title={<I18NText>{assocationType === OrgAssociationType.Employee ? 'Employees' : 'Consultants'}</I18NText>} />}
                toolbar={
                    <ButtonGroup>
                         <Button
                            sx={{
                                backgroundColor: theme.palette.secondary.main,
                                color: theme.palette.getContrastText(theme.palette.secondary.main),
                                '&:hover':{
                                    backgroundColor: theme.palette.secondary.light,
                                },
                                marginRight: '1em'
                            }} 
                            onClick={handleShowAdd}>Invite Person</Button>
                        <SearchBox />
                    </ButtonGroup>
                }
                fillHeight>
                <ResponsiveCollection
                    viewMode={viewport.mode}
                    items={visibleRows}
                    getKey={row => row.Id}
                    renderCard={renderUserCard}
                    desktop={desktopGrid}
                    ariaLabel={assocationType === OrgAssociationType.Employee ? 'Employees' : 'Consultants'}
                    emptyState={<Typography sx={{ p: 2 }} color="text.secondary">No people found</Typography>}
                />
            </PanelSegment>
        </BusySpinner>
        <ResponsiveDialog
            aria-labelledby="customized-dialog-title"
            open={showInvitePopup}
            fullWidth
            tabletFullScreen="portrait"
            PaperProps={{ sx: { '& > .responsive-dialog-busy': { display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' } } }}
        >
            <BusySpinner busy={busy} className="responsive-dialog-busy">
                <ResponsiveDialogHeader
                    id="customized-dialog-title"
                    title={`Invite ${assocationType === OrgAssociationType.Employee ? 'Employee' : 'Consultant'}`}
                    onClose={() => setShowInvitePopup(false)}
                />
                <DialogContent dividers sx={{ padding: theme.spacing(2), minHeight: 0, overflowY: 'auto' }}>
                    <div>
                        <TextField
                            required
                            fullWidth
                            id="outlined-required"
                            label="Email Addresses"
                            placeholder="joe@yourdomain.com, sally@yourdomain.com"
                            variant="outlined"
                            value={inviteEmails ?? ''}

                            onChange={(event) => setInviteEmails(event.target.value)} />
                    </div>
                    <ConditionalDisplay ifTrue={assocationType === OrgAssociationType.Employee}>
                        <FormControl sx={{ marginTop: '2em' }}>
                            <FormLabel id="employee-roles-selector">
                                <Typography>Select this persons role</Typography>
                                <Typography fontSize="small"> (can be modified later)</Typography>
                            </FormLabel>
                            <RadioGroup
                                sx={{ marginTop: '1em' }}
                                defaultValue="User"
                                name="employee-roles-radio-group"
                                aria-labelledby="employee-roles-selector"
                                onChange={(e) => setRoleTemplate(e.target.value)}>
                                <FormControlLabel label="User (basic read-only)" value="User" control={<Radio />} />
                                <FormControlLabel label="Assessor (able to perform Assessments)" value="Assessor" control={<Radio />} />
                                <FormControlLabel label="Templater (full access to Proficiencies and Templates)" value="Templater" control={<Radio />} />
                                <FormControlLabel label="HR (full access to the core of the system)" value="HR" control={<Radio />} />
                                <FormControlLabel label="Admin (full access)" value="Admin" control={<Radio />} />
                            </RadioGroup>
                        </FormControl>
                    </ConditionalDisplay>
                </DialogContent>
                <StickyDialogActions phoneLayout="stacked" sx={{ margin: 0, padding: theme.spacing(1) }}>
                    <Button disabled={busy} onClick={() => setShowInvitePopup(false)} variant='text' color={IsEmail(inviteEmails) ? "primary" : "inherit"}>Cancel</Button>
                    <Button disabled={busy || !IsEmail(inviteEmails)} onClick={handleInvite} variant='outlined' color={IsEmail(inviteEmails) ? "inherit" : "primary"}>Send Invite</Button>
                </StickyDialogActions>
            </BusySpinner>
        </ResponsiveDialog>
    </>;
}

export default OrgUsersDisplay;
